import { Button, Tabs, Tag } from "antd";
import AuthorisedContainer from "components/AuthorisedContainer";
import { PermissionSetMap } from "constants/RBACConstants";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { BaseIntegrationConfig } from "restAPI/GenericIntegrationAPI";
import ModalUtil from "utils/modalUtil";

import GenericIntegrationListing, {
  GenericIntegrationListingRef,
} from "./GenericIntegrationListing";

export interface TabConfig {
  key: string;
  label: string;
  integrationType: string;
  customColumns?: any[];
  showTriggerScan?: boolean;
}

export interface GenericIntegrationViewProps<T extends BaseIntegrationConfig> {
  tabs: TabConfig[];
  FormComponent: React.ComponentType<{
    configData?: T;
    onUpdateConfigComponent: () => void;
    configList?: T[];
  }>;
  onUpdateConfigComponent?: () => void;
  addButtonText?: string;
  name?: string;
  /** When true, shows tabs UI even with a single tab. Useful for future tab additions. */
  showTabsAlways?: boolean;
}

export default function GenericIntegrationView<
  T extends BaseIntegrationConfig,
>({
  tabs,
  FormComponent,
  onUpdateConfigComponent,
  addButtonText = "Add Configuration",
  name,
  showTabsAlways = false,
}: GenericIntegrationViewProps<T>) {
  const listingRefs = useRef<Record<string, GenericIntegrationListingRef>>({});
  const [configCounts, setConfigCounts] = useState<Record<string, number>>({});

  const handleRefresh = useCallback(
    (tabKey?: string) => {
      if (tabKey) {
        listingRefs.current[tabKey]?.refresh();
      } else {
        Object.values(listingRefs.current).forEach((r) => r?.refresh());
      }
      onUpdateConfigComponent?.();
    },
    [onUpdateConfigComponent],
  );

  const openConfigForm = useCallback(() => {
    ModalUtil.show({
      content: (
        <FormComponent
          onUpdateConfigComponent={() => handleRefresh()}
          configList={[]}
        />
      ),
    });
  }, [handleRefresh, FormComponent]);

  const handleConfigCountChange = useCallback(
    (tabKey: string, count: number) => {
      setConfigCounts((prev) => ({ ...prev, [tabKey]: count }));
    },
    [],
  );

  const setListingRef = useCallback(
    (tabKey: string, ref: GenericIntegrationListingRef | null) => {
      if (ref) listingRefs.current[tabKey] = ref;
    },
    [],
  );

  const configCountHandlers = useMemo(() => {
    const handlers: Record<string, (count: number) => void> = {};
    tabs.forEach((tab) => {
      handlers[tab.key] = (count: number) =>
        handleConfigCountChange(tab.key, count);
    });
    return handlers;
  }, [tabs, handleConfigCountChange]);

  const renderTabContent = useCallback(
    (tab: TabConfig) => (
      <>
        <div className="text-right m-b-sm">
          <AuthorisedContainer permission={PermissionSetMap.WRITE_TOOLS}>
            <Button size="small" type="primary" onClick={openConfigForm}>
              {addButtonText}
            </Button>
          </AuthorisedContainer>
        </div>
        <GenericIntegrationListing<T>
          ref={(ref) => setListingRef(tab.key, ref)}
          integrationType={tab.integrationType}
          onUpdateConfigComponent={onUpdateConfigComponent}
          onConfigCountChange={configCountHandlers[tab.key]}
          customColumns={tab.customColumns}
          FormComponent={FormComponent}
          name={name}
          showTriggerScan={tab.showTriggerScan}
        />
      </>
    ),
    [
      openConfigForm,
      addButtonText,
      onUpdateConfigComponent,
      configCountHandlers,
      setListingRef,
      FormComponent,
      name,
    ],
  );

  if (tabs.length === 1 && !showTabsAlways) {
    return (
      <div className="generic-integration-view">
        {renderTabContent(tabs[0])}
      </div>
    );
  }

  return (
    <Tabs defaultActiveKey={tabs[0]?.key}>
      {tabs.map((tab) => (
        <Tabs.TabPane
          tab={
            <>
              {tab.label}{" "}
              <Tag className="m-0">{configCounts[tab.key] ?? 0}</Tag>
            </>
          }
          key={tab.key}
        >
          {renderTabContent(tab)}
        </Tabs.TabPane>
      ))}
    </Tabs>
  );
}
